import {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Checkbox,
    InputAdornment,
    TextField,
    Tooltip,
    styled,
} from "@mui/material";

import { Info } from "@mui/icons-material";

import { GridRowId } from "@mui/x-data-grid";
import { FormikErrors, FormikTouched } from "formik";

import { Paper } from "../../../components/common";

import { EntitledVariants } from "./EntitledVariants";
import { PrereqProductVariants } from "./PrereqProductVariants";
import {
    DiscountSection,
    IPriceRuleForm,
    Row,
    SectionTitle,
    StyledTooltip,
    TextFieldContainer,
    disableScrollWheelInput,
} from "./common";

const Title = styled("h1")`
    font-family: Poppins;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0%;
`;

const StyledCheckbox = styled(Checkbox)`
    width: 20px;
    height: 20px;

    margin-right: ${({ theme }) => theme.spacing(1)};
`;

const ProductContainer = styled("div")`
    display: flex;
    flex-direction: column;
`;

const CheckedContainer = styled("div")`
    display: flex;
    justify-content: start;
    align-items: center;
`;

const VariantContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
`;

export interface ICartPrereqsSectionProps {
    values: IPriceRuleForm;
    touched: FormikTouched<IPriceRuleForm>;
    errors: FormikErrors<IPriceRuleForm>;
    onChange: (e: React.ChangeEvent<any>) => void;
    onBlur: {
        (e: React.FocusEvent<any, Element>): void;
        <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
    };
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
    ) => Promise<void> | Promise<FormikErrors<IPriceRuleForm>>;
}

export const CartPrereqsSection: FunctionComponent<ICartPrereqsSectionProps> = (
    props: ICartPrereqsSectionProps,
): ReactElement => {
    const { values, errors, touched, onChange, onBlur, setFieldValue } = props;
    const [checked, setChecked] = useState<boolean>(false);

    const handlePrereqVariantIdsChange = useCallback(
        (newValue: GridRowId[] | undefined) => {
            setFieldValue("variantIds", newValue);
        },
        [setFieldValue],
    );

    const handleDiscountVariantIdsChange = useCallback(
        (newValue: GridRowId[] | undefined) => {
            setFieldValue("entitledVariantIds", newValue);
            if (checked) {
                setFieldValue("variantIds", newValue);
            }
        },
        [setFieldValue, checked, values],
    );

    const handleCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = event.target.checked;
            setChecked(isChecked);
            if (!isChecked) {
                setFieldValue("variantIds", values.variantIds);
                setFieldValue("entitledVariantIds", values.entitledVariantIds);
            }

            if (isChecked) {
                setFieldValue("variantIds", values.entitledVariantIds);
            }
        },
        [setFieldValue, values],
    );

    return (
        <Paper>
            <DiscountSection>
                <SectionTitle>Cart prerequisites</SectionTitle>
                <Row>
                    <TextFieldContainer>
                        <TextField
                            type="number"
                            fullWidth={true}
                            name="prereqSubtotalMin"
                            value={values.prereqSubtotalMin}
                            onChange={onChange}
                            label="Min TSP"
                            size="small"
                            variant="outlined"
                            onBlur={onBlur}
                            onWheel={disableScrollWheelInput}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        ₹
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <StyledTooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            title={
                                                "The minimum Total Selling Price (TSP) which is needed, if any, to apply this discount."
                                            }
                                        >
                                            <Info
                                                htmlColor="#626262"
                                                fontSize="small"
                                            />
                                        </StyledTooltip>
                                    </InputAdornment>
                                ),
                            }}
                            error={
                                Boolean(errors.prereqSubtotalMin) &&
                                touched.prereqSubtotalMin
                            }
                            helperText={
                                touched.prereqSubtotalMin &&
                                errors.prereqSubtotalMin
                            }
                        />
                    </TextFieldContainer>
                    <TextFieldContainer />
                </Row>
                <Row>
                    <TextFieldContainer>
                        <TextField
                            name="prereqQuantityMin"
                            fullWidth={true}
                            value={values.prereqQuantityMin}
                            onChange={onChange}
                            onBlur={onBlur}
                            label="Min quantity of items"
                            size="small"
                            variant="outlined"
                            type="number"
                            onWheel={disableScrollWheelInput}
                            error={
                                Boolean(errors.prereqQuantityMin) &&
                                touched.prereqQuantityMin
                            }
                            helperText={
                                touched.prereqQuantityMin &&
                                errors.prereqQuantityMin
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <StyledTooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            title={
                                                "The minimum number of items required in the cart, irrespective of the product type, to be able to apply the discount."
                                            }
                                        >
                                            <Info
                                                htmlColor="#626262"
                                                fontSize="small"
                                            />
                                        </StyledTooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer />
                </Row>
                <Row>
                    <TextFieldContainer>
                        <TextField
                            fullWidth={true}
                            name="prereqVariantsRequired"
                            value={values.prereqVariantsRequired}
                            placeholder="Quantity"
                            type="number"
                            label="Prerequisite variants required"
                            onChange={onChange}
                            size="small"
                            variant="outlined"
                            onBlur={onBlur}
                            onWheel={disableScrollWheelInput}
                            error={
                                Boolean(errors.prereqVariantsRequired) &&
                                touched.prereqVariantsRequired
                            }
                            helperText={
                                touched.prereqVariantsRequired &&
                                errors.prereqVariantsRequired
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <StyledTooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            title={
                                                "The minimum number of prerequisite product variants required in the cart to be able to apply the discount."
                                            }
                                        >
                                            <Info
                                                htmlColor="#626262"
                                                fontSize="small"
                                            />
                                        </StyledTooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer />
                </Row>

                <ProductContainer>
                    <CheckedContainer>
                        <StyledCheckbox
                            checked={checked}
                            onChange={handleCheckboxChange}
                        />
                        <Title>Product Specific Discount</Title>
                    </CheckedContainer>
                    {!checked && (
                        <>
                            <VariantContainer>
                                <Title>Prerequisite Variants</Title>
                                <StyledTooltip
                                    enterTouchDelay={0}
                                    placement="top"
                                    title={
                                        "Specifies product variants which are prerequisite in the cart for discount to be applicable"
                                    }
                                >
                                    <Info
                                        htmlColor="#626262"
                                        fontSize="small"
                                    />
                                </StyledTooltip>
                            </VariantContainer>
                            <PrereqProductVariants
                                prereqVariantIds={values.variantIds}
                                onChange={handlePrereqVariantIdsChange}
                            />
                        </>
                    )}

                    <VariantContainer>
                        <Title>Variants on Discount</Title>
                        <StyledTooltip
                            enterTouchDelay={0}
                            placement="top"
                            title={
                                "Specifies product variants on which discounts will be applied"
                            }
                        >
                            <Info htmlColor="#626262" fontSize="small" />
                        </StyledTooltip>
                    </VariantContainer>

                    <EntitledVariants
                        entitledVariantIds={values.entitledVariantIds}
                        onChange={handleDiscountVariantIdsChange}
                    />
                </ProductContainer>
            </DiscountSection>
        </Paper>
    );
};
